import { Component, OnInit, inject, signal } from '@angular/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { lastValueFrom } from 'rxjs';

import { EmployeeProject } from 'src/app/shared/models';
import {
  AlertService,
  AlertType,
  AuthService,
  EmployeeService,
  ReportService,
  RequestService,
} from 'src/app/shared/services';
import { SharedModule } from 'src/app/shared/shared.module';
import { EmployeeReportExportComponent } from 'src/app/shared/components/employee-report-export/employee-report-export.component';
import { SubmitReportComponent } from 'src/app/admin/submit-report/submit-report.component';

@Component({
  selector: 'app-client-hours',
  templateUrl: './client-hours.component.html',
  styleUrls: ['./client-hours.component.scss'],
  imports: [
    EmployeeReportExportComponent,
    MatProgressSpinnerModule,
    SharedModule,
    SubmitReportComponent,
    TranslateModule,
  ],
})
export class ClientHoursComponent implements OnInit {
  private alertService = inject(AlertService);
  private requestService = inject(RequestService);
  private reportService = inject(ReportService);
  private employeeService = inject(EmployeeService);
  private authService = inject(AuthService);
  private translate = inject(TranslateService);

  projects = signal<EmployeeProject[]>([]);
  isLoading = false;

  ngOnInit() {
    this.employeeService.loadMyProjects(this.authService.userId()).then(this.projects.set);
  }

  async onSubmit({
    fromDate,
    project,
    toDate,
  }: {
    fromDate: string;
    project?: EmployeeProject;
    toDate: string;
  }) {
    this.isLoading = true;

    try {
      const buffer = await lastValueFrom(
        this.requestService.downloadLoggedHours(fromDate, toDate, project.id),
      );

      this.reportService.saveAsExcelFile({
        buffer,
        fileName: this.buildFileName(project.name, fromDate, toDate),
      });
    } catch (error) {
      this.alertService.showAlert(
        this.translate.instant('ADMIN.CLIENT_HOURS.EXPORT_ERROR').toString(),
        AlertType.error,
      );
    } finally {
      this.isLoading = false;
    }
  }

  async onSubmitSynechron({
    fromDate,
    project,
    toDate,
  }: {
    fromDate: string;
    project?: EmployeeProject;
    toDate: string;
  }) {
    this.isLoading = true;

    try {
      const buffer = await lastValueFrom(
        this.requestService.downloadLoggedHoursSynechron(fromDate, toDate, project.id),
      );

      this.reportService.saveAsExcelFile({
        buffer,
        fileName: this.buildFileName(project.name, fromDate, toDate),
      });
    } catch (error) {
      this.alertService.showAlert(
        this.translate.instant('ADMIN.CLIENT_HOURS.EXPORT_ERROR').toString(),
        AlertType.error,
      );
    } finally {
      this.isLoading = false;
    }
  }

  private buildFileName(projectName: string, fromDate: string, toDate: string): string {
    const from = moment(fromDate);
    const to = moment(toDate);
    const isSingleMonth =
      from.isSame(from.clone().startOf('month'), 'day') &&
      to.isSame(from.clone().endOf('month'), 'day');

    return isSingleMonth
      ? `Workreport_${projectName}_${from.format('MM/YYYY')}.xlsx`
      : `Workreport_${projectName}_${from.format('YYYY-MM-DD')}_${to.format('YYYY-MM-DD')}.xlsx`;
  }
}
